/**
 * Extract a color palette from an image File.
 *
 * Approach: load image → draw to a downsampled canvas → run k-means clustering
 * (k=10) over the pixels with histogram-seeded centroids → score each cluster
 * by visual interest (saturation × √count) → pick the most interesting
 * clusters as "main colors" so colorful pixels beat dominant neutrals.
 *
 * The previous histogram-only approach lost to gray/cream backgrounds because
 * neutrals are usually the most frequent pixels in any photo. K-means + an
 * interest score lets the actually-distinctive colors win even when there are
 * fewer of them.
 *
 * Returns an object suitable for generating a custom theme CSS block.
 */

const SAMPLE_DIM = 220; // downsample longest edge to this many px
const K = 16;           // number of clusters — more candidates for outlier diversity
const KMEANS_ITER = 14;
const MAIN_COLOR_COUNT = 6;

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

function rgbToHex(r, g, b) {
  const toHex = (n) => n.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

function luminance(r, g, b) {
  // Perceptual luminance (Rec. 709)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function saturation(r, g, b) {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max === 0) return 0;
  return (max - min) / max;
}

function colorDistance(a, b) {
  const dr = a.r - b.r;
  const dg = a.g - b.g;
  const db = a.b - b.b;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

function colorDistanceSq(a, b) {
  const dr = a.r - b.r;
  const dg = a.g - b.g;
  const db = a.b - b.b;
  return dr * dr + dg * dg + db * db;
}

/**
 * K-means over an array of {r,g,b} pixels.
 * Centroids seeded from top histogram buckets so the result is deterministic.
 */
function kmeans(pixels, k = K, maxIter = KMEANS_ITER) {
  if (pixels.length === 0) return [];

  // Seed centroids from top histogram buckets (4-bit per channel)
  const buckets = new Map();
  for (const p of pixels) {
    const key = ((p.r & 0xf0) << 16) | ((p.g & 0xf0) << 8) | (p.b & 0xf0);
    buckets.set(key, (buckets.get(key) || 0) + 1);
  }
  const sortedBuckets = Array.from(buckets.entries()).sort((a, b) => b[1] - a[1]);
  let centroids = [];
  for (const [key] of sortedBuckets) {
    const c = {
      r: ((key >> 16) & 0xff) | 0x08,
      g: ((key >> 8) & 0xff) | 0x08,
      b: (key & 0xff) | 0x08,
    };
    // Only seed centroids that aren't too close to existing seeds (better spread)
    if (centroids.every((d) => colorDistanceSq(c, d) > 1500)) {
      centroids.push(c);
    }
    if (centroids.length >= k) break;
  }
  while (centroids.length < k) {
    centroids.push({
      r: Math.floor(Math.random() * 256),
      g: Math.floor(Math.random() * 256),
      b: Math.floor(Math.random() * 256),
    });
  }

  let clusters = [];
  for (let iter = 0; iter < maxIter; iter++) {
    clusters = centroids.map(() => ({ r: 0, g: 0, b: 0, count: 0 }));
    for (const p of pixels) {
      let minD = Infinity;
      let minI = 0;
      for (let i = 0; i < k; i++) {
        const d = colorDistanceSq(p, centroids[i]);
        if (d < minD) {
          minD = d;
          minI = i;
        }
      }
      const cl = clusters[minI];
      cl.r += p.r;
      cl.g += p.g;
      cl.b += p.b;
      cl.count++;
    }
    let moved = 0;
    const next = clusters.map((cl, i) => {
      if (cl.count === 0) return centroids[i];
      const nc = {
        r: Math.round(cl.r / cl.count),
        g: Math.round(cl.g / cl.count),
        b: Math.round(cl.b / cl.count),
      };
      moved += colorDistance(nc, centroids[i]);
      return nc;
    });
    centroids = next;
    if (moved < 2) break; // converged
  }

  // Return clusters with their final centroid + pixel count, dropping empty ones
  return centroids
    .map((c, i) => ({ r: c.r, g: c.g, b: c.b, count: clusters[i].count }))
    .filter((c) => c.count > 0);
}

function rgba(c, alpha) {
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${alpha})`;
}

function tint(c, towardWhite, amount) {
  // amount 0..1, towardWhite=true lightens, false darkens toward black
  const target = towardWhite ? 255 : 0;
  return {
    r: Math.round(c.r + (target - c.r) * amount),
    g: Math.round(c.g + (target - c.g) * amount),
    b: Math.round(c.b + (target - c.b) * amount),
  };
}

// ── WCAG contrast helpers ──
// https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
function srgbToLinear(channel) {
  const v = channel / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function relativeLuminance(c) {
  const r = srgbToLinear(c.r);
  const g = srgbToLinear(c.g);
  const b = srgbToLinear(c.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Brighten or darken a color until it meets a target contrast ratio against
 * the given background. Direction is determined by which side of the bg the
 * color is closer to (push it further away). Preserves hue.
 *
 * Falls back to pure white/black if it can't reach the target.
 */
function ensureContrast(color, bg, targetRatio = 4.5) {
  if (contrastRatio(color, bg) >= targetRatio) return color;

  const bgIsLight = relativeLuminance(bg) > 0.5;
  // Push the color away from the bg's luminance
  const toward = bgIsLight ? 0 : 255; // dark text on light, light text on dark

  let lo = 0;
  let hi = 1;
  let best = { ...color };
  // Binary-search the smallest tint amount that crosses the threshold
  for (let i = 0; i < 16; i++) {
    const mid = (lo + hi) / 2;
    const candidate = {
      r: Math.round(color.r + (toward - color.r) * mid),
      g: Math.round(color.g + (toward - color.g) * mid),
      b: Math.round(color.b + (toward - color.b) * mid),
    };
    if (contrastRatio(candidate, bg) >= targetRatio) {
      best = candidate;
      hi = mid;
    } else {
      lo = mid;
    }
  }
  return best;
}

/**
 * Step 1: Read the file → cluster pixels → return scored clusters.
 * Cheap to call once and cache; the resulting clusters are JSON-serializable
 * so we can re-derive themes from them without re-reading the file.
 */
export async function extractClusters(file) {
  const img = await loadImage(file);

  const longest = Math.max(img.width, img.height);
  const scale = longest > SAMPLE_DIM ? SAMPLE_DIM / longest : 1;
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);

  const pixels = [];
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 128) continue;
    pixels.push({ r: data[i], g: data[i + 1], b: data[i + 2] });
  }
  if (pixels.length === 0) pixels.push({ r: 128, g: 128, b: 128 });

  let clusters = kmeans(pixels, K, KMEANS_ITER);

  const merged = [];
  for (const c of clusters.sort((a, b) => b.count - a.count)) {
    const near = merged.find((d) => colorDistance(c, d) < 25);
    if (near) {
      const total = near.count + c.count;
      near.r = Math.round((near.r * near.count + c.r * c.count) / total);
      near.g = Math.round((near.g * near.count + c.g * c.count) / total);
      near.b = Math.round((near.b * near.count + c.b * c.count) / total);
      near.count = total;
    } else {
      merged.push({ ...c });
    }
  }
  clusters = merged;
  if (clusters.length === 0) clusters.push({ r: 128, g: 128, b: 128, count: 1 });

  return clusters.map((c) => ({
    ...c,
    sat: saturation(c.r, c.g, c.b),
    lum: luminance(c.r, c.g, c.b),
  }));
}

/**
 * Step 2: Build a full theme from already-extracted clusters.
 * `mode` can be 'auto' | 'light' | 'dark' — auto detects from mean luminance.
 * Exposed so the UI can re-derive a theme as light/dark without re-uploading.
 */
export function buildThemeFromClusters(scored, mode = "auto") {

  if (!scored || scored.length === 0) {
    scored = [{ r: 128, g: 128, b: 128, count: 1, sat: 0, lum: 128 }];
  }

  // ── Split clusters into NEUTRALS (UI surfaces) and ACCENTS (highlights) ──
  // Per design principle: desaturated colors are good for backgrounds, text,
  // borders. Saturated colors should only be used as accent/highlight pops.
  // Threshold lowered to 0.14 so muted blues/golds still register as accents.
  const SAT_NEUTRAL = 0.14;
  const neutrals = scored
    .filter((c) => c.sat < SAT_NEUTRAL)
    .sort((a, b) => b.count - a.count);
  const accentPool = scored
    .filter((c) => c.sat >= SAT_NEUTRAL)
    .sort((a, b) => b.sat * Math.sqrt(b.count) - a.sat * Math.sqrt(a.count));

  // Determine isLight: respect explicit mode override; otherwise auto-detect
  // by weighting cluster luminance by pixel count.
  let isLight;
  if (mode === "light") {
    isLight = true;
  } else if (mode === "dark") {
    isLight = false;
  } else {
    const totalPixels = scored.reduce((s, c) => s + c.count, 0);
    const meanLum =
      scored.reduce((s, c) => s + c.lum * c.count, 0) / Math.max(1, totalPixels);
    isLight = meanLum > 140;
  }

  // ── Build the displayed "main colors" with farthest-point sampling ──
  //
  // Strategy:
  //   1. Seed with the dominant neutral (background tone) and its high-contrast
  //      opposite — this anchors the palette in the bg/text pair.
  //   2. Then, for each remaining slot, pick the cluster that maximizes its
  //      MINIMUM distance to all already-picked colors. This naturally pulls
  //      OUTLIERS (e.g. a small patch of blue in an otherwise white kitchen)
  //      because they're farther away than yet-another-neutral would be.
  //   3. Boost saturated colors during the score so they edge out near-duplicate
  //      neutrals when distances are similar.
  const sortedNeutralsByLum = [...neutrals].sort((a, b) => a.lum - b.lum);
  const lightestNeutral = sortedNeutralsByLum[sortedNeutralsByLum.length - 1] || scored[0];
  const darkestNeutral = sortedNeutralsByLum[0] || scored[0];

  const mainPicks = [];
  const pushUnique = (c) => {
    if (!c) return;
    if (mainPicks.every((d) => colorDistance(c, d) > 18)) {
      mainPicks.push(c);
    }
  };

  // Seed with bg + text pair (high-contrast neutrals)
  if (isLight) {
    pushUnique(lightestNeutral);
    pushUnique(darkestNeutral);
  } else {
    pushUnique(darkestNeutral);
    pushUnique(lightestNeutral);
  }

  // Farthest-point sampling for the rest — favors outliers over near-duplicates
  const remaining = scored.filter(
    (c) => !mainPicks.some((p) => colorDistance(c, p) < 12)
  );
  while (mainPicks.length < MAIN_COLOR_COUNT && remaining.length > 0) {
    let bestIdx = -1;
    let bestScore = -Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const c = remaining[i];
      // Min distance to any already-picked color
      let minDist = Infinity;
      for (const p of mainPicks) {
        const d = colorDistance(c, p);
        if (d < minDist) minDist = d;
      }
      // Score: distance × (1 + saturation_boost) × (1 + frequency_boost)
      // Saturation boost ensures vivid outliers beat washed-out outliers.
      // Frequency boost (small) prevents picking single-pixel noise.
      const satBoost = 1 + c.sat * 1.2;
      const freqBoost = 1 + Math.log10(1 + c.count) * 0.15;
      const score = minDist * satBoost * freqBoost;
      if (score > bestScore) {
        bestScore = score;
        bestIdx = i;
      }
    }
    if (bestIdx === -1) break;
    mainPicks.push(remaining[bestIdx]);
    remaining.splice(bestIdx, 1);
  }

  while (mainPicks.length < MAIN_COLOR_COUNT) mainPicks.push(scored[0]);
  const mainColors = mainPicks.slice(0, MAIN_COLOR_COUNT);

  // ── Pick background and heading text from the NEUTRAL pool ──
  // bg should sit at the dominant luminance end (most pixels). Text contrasts.
  // If neutrals don't span enough range, fall back to the global byLum extremes.
  const allByLum = [...scored].sort((a, b) => a.lum - b.lum);
  const neutralByLum = [...neutrals].sort((a, b) => a.lum - b.lum);
  const fallbackBg = isLight ? allByLum[allByLum.length - 1] : allByLum[0];
  const fallbackHeading = isLight ? allByLum[0] : allByLum[allByLum.length - 1];
  const bg =
    (isLight
      ? neutralByLum[neutralByLum.length - 1]
      : neutralByLum[0]) || fallbackBg;
  const heading =
    (isLight ? neutralByLum[0] : neutralByLum[neutralByLum.length - 1]) ||
    fallbackHeading;

  // Force WCAG AAA contrast (7:1) between bg and heading text.
  // bg stays roughly where it was — we adjust the heading text to fit.
  const adjBg = bg;
  const adjHeading = ensureContrast(heading, adjBg, 7);

  // ── Build accent pool: ONLY saturated colors are accents ──
  // (Per design rule: desaturated → UI surfaces, saturated → highlights only.)
  // Each accent is force-adjusted to meet WCAG ~4.5:1 contrast against the
  // background so it's actually readable when used as text/icon color.
  const ACCENT_CONTRAST_TARGET = 4.5;
  const accents = [];
  const seen = new Set();
  const pushAccent = (rawC) => {
    if (!rawC) return;
    const c = ensureContrast(rawC, adjBg, ACCENT_CONTRAST_TARGET);
    const key = `${c.r}-${c.g}-${c.b}`;
    if (seen.has(key)) return;
    seen.add(key);
    accents.push(c);
  };
  accentPool.forEach(pushAccent);

  // If the image has no saturated colors at all, derive synthetic accents by
  // boosting the contrast of the most mid-luminance neutrals.
  if (accents.length === 0) {
    const midNeutral = [...neutrals].sort((a, b) => Math.abs(127 - a.lum) - Math.abs(127 - b.lum))[0] || scored[0];
    pushAccent(midNeutral);
  }

  // Assign 7 accents (cycling if too few). Each pick is already contrast-safe.
  const pick = (i) => accents[i % accents.length];
  const accentBlue = pick(0);
  const accentEmerald = pick(1);
  const accentAmber = pick(2);
  const accentPurple = pick(3);
  const accentRose = pick(4);
  const accentCyan = pick(5);
  const accentRed = pick(6);

  // Build the palette object
  const bodyText = isLight ? tint(adjHeading, true, 0.25) : tint(adjHeading, false, 0.25);
  const secondaryText = isLight ? tint(adjHeading, true, 0.45) : tint(adjHeading, false, 0.40);
  const mutedText = isLight ? tint(adjHeading, true, 0.60) : tint(adjHeading, false, 0.55);
  const faintText = isLight ? tint(adjHeading, true, 0.75) : tint(adjHeading, false, 0.70);

  const sidebarStart = isLight ? tint(adjBg, true, 0.15) : tint(adjBg, true, 0.04);
  const sidebarEnd = isLight ? adjBg : tint(adjBg, false, 0.10);

  const overlayBase = isLight ? "60, 42, 34" : "244, 241, 234";

  return {
    isLight,
    sourceColors: mainColors.map((c) => rgbToHex(c.r, c.g, c.b)),
    vars: {
      "--bg-root": rgbToHex(adjBg.r, adjBg.g, adjBg.b),
      "--bg-sidebar": `linear-gradient(180deg, ${rgbToHex(sidebarStart.r, sidebarStart.g, sidebarStart.b)} 0%, ${rgbToHex(sidebarEnd.r, sidebarEnd.g, sidebarEnd.b)} 100%)`,
      "--bg-card-frosted": `linear-gradient(135deg, ${rgba(tint(adjBg, isLight, 0.10), 0.92)} 0%, ${rgba(adjBg, 0.86)} 45%, ${rgba(tint(adjBg, isLight, 0.08), 0.92)} 100%)`,
      "--border-card-frosted": `rgba(${overlayBase}, 0.12)`,
      "--border-card-base": `rgba(${overlayBase}, 0.08)`,
      "--border-subtle": `rgba(${overlayBase}, 0.10)`,
      "--border-row": `rgba(${overlayBase}, 0.06)`,
      "--bg-row-hover": `rgba(${overlayBase}, 0.04)`,
      "--bg-input": `rgba(${overlayBase}, 0.05)`,
      "--border-input": `rgba(${overlayBase}, 0.16)`,
      "--bg-progress-track": `rgba(${overlayBase}, 0.08)`,
      "--glow-color": rgba(accentBlue, 0.10),
      "--bracket-tl": rgba(accentBlue, 0.30),
      "--bracket-tr": rgba(accentBlue, 0.25),
      "--bracket-bl": rgba(accentBlue, 0.20),
      "--bracket-br": rgba(accentBlue, 0.15),
      "--text-heading": rgbToHex(adjHeading.r, adjHeading.g, adjHeading.b),
      "--text-body": rgbToHex(bodyText.r, bodyText.g, bodyText.b),
      "--text-secondary": rgbToHex(secondaryText.r, secondaryText.g, secondaryText.b),
      "--text-muted": rgbToHex(mutedText.r, mutedText.g, mutedText.b),
      "--text-faint": rgbToHex(faintText.r, faintText.g, faintText.b),
      "--select-bg": `rgba(${overlayBase}, 0.04)`,
      "--avatar-bg": `rgba(${overlayBase}, 0.06)`,
      "--avatar-border": `rgba(${overlayBase}, 0.12)`,
      "--avatar-icon": rgbToHex(mutedText.r, mutedText.g, mutedText.b),
    },
    accents: {
      blue: rgbToHex(accentBlue.r, accentBlue.g, accentBlue.b),
      emerald: rgbToHex(accentEmerald.r, accentEmerald.g, accentEmerald.b),
      amber: rgbToHex(accentAmber.r, accentAmber.g, accentAmber.b),
      purple: rgbToHex(accentPurple.r, accentPurple.g, accentPurple.b),
      rose: rgbToHex(accentRose.r, accentRose.g, accentRose.b),
      cyan: rgbToHex(accentCyan.r, accentCyan.g, accentCyan.b),
      red: rgbToHex(accentRed.r, accentRed.g, accentRed.b),
    },
    sidebarColors: {
      headingText: rgbToHex(adjHeading.r, adjHeading.g, adjHeading.b),
      bodyText: rgbToHex(bodyText.r, bodyText.g, bodyText.b),
      secondaryText: rgbToHex(secondaryText.r, secondaryText.g, secondaryText.b),
      accentText: rgbToHex(accentBlue.r, accentBlue.g, accentBlue.b),
      accentBg: rgba(accentBlue, 0.16),
      hoverBg: `rgba(${overlayBase}, 0.06)`,
      sidebarGradient: `linear-gradient(180deg, ${rgbToHex(sidebarStart.r, sidebarStart.g, sidebarStart.b)} 0%, ${rgbToHex(sidebarEnd.r, sidebarEnd.g, sidebarEnd.b)} 100%)`,
      sidebarBorder: `rgba(${overlayBase}, 0.10)`,
      border: `rgba(${overlayBase}, 0.10)`,
      borderSoft: `rgba(${overlayBase}, 0.07)`,
    },
    // Keep the source clusters embedded so the UI can re-derive the theme
    // as light or dark without re-uploading the original image.
    clusters: scored,
  };
}

/**
 * Convenience: extract clusters and build a theme in one call.
 */
export async function extractPalette(file, mode = "auto") {
  const clusters = await extractClusters(file);
  return buildThemeFromClusters(clusters, mode);
}

/**
 * Force every accent in a palette to meet a minimum WCAG contrast ratio
 * against the palette's background. Used at CSS generation time so existing
 * saved themes (which may have been generated before contrast enforcement
 * existed) get auto-corrected on the fly.
 */
function enforceAccentContrast(palette, targetRatio = 4.5) {
  if (!palette || !palette.accents || !palette.vars) return palette;
  const bgHex = palette.vars["--bg-root"];
  if (!bgHex) return palette;
  const bg = hexToRgb(bgHex);

  const fix = (hex) => {
    const c = hexToRgb(hex);
    const adjusted = ensureContrast(c, bg, targetRatio);
    return rgbToHex(adjusted.r, adjusted.g, adjusted.b);
  };

  return {
    ...palette,
    accents: {
      blue: fix(palette.accents.blue),
      emerald: fix(palette.accents.emerald),
      amber: fix(palette.accents.amber),
      purple: fix(palette.accents.purple),
      rose: fix(palette.accents.rose),
      cyan: fix(palette.accents.cyan),
      red: fix(palette.accents.red),
    },
  };
}

/**
 * Convert a palette object into a CSS string that defines [data-theme="custom"]
 * including all variable + Tailwind class overrides used by the app.
 *
 * Accents are force-bumped to WCAG 4.5:1 against the bg before CSS generation,
 * so saved themes from before that contrast pass existed get auto-fixed.
 */
export function generateCustomThemeCSS(rawPalette) {
  if (!rawPalette) return "";
  const palette = enforceAccentContrast(rawPalette, 4.5);
  const v = palette.vars;
  const a = palette.accents;
  const overlayBase = palette.isLight ? "60, 42, 34" : "244, 241, 234";

  const varBlock = Object.entries(v)
    .map(([k, val]) => `  ${k}: ${val};`)
    .join("\n");

  return `
[data-theme="custom"] {
${varBlock}
}

[data-theme="custom"] .text-gray-100,
[data-theme="custom"] .text-gray-200 { color: var(--text-heading); }
[data-theme="custom"] .text-gray-300 { color: var(--text-body); }
[data-theme="custom"] .text-gray-400 { color: var(--text-secondary); }
[data-theme="custom"] .text-gray-500 { color: var(--text-muted); }
[data-theme="custom"] .text-gray-600 { color: var(--text-faint); }

[data-theme="custom"] .hover\\:text-gray-100:hover,
[data-theme="custom"] .hover\\:text-gray-200:hover { color: var(--text-heading); }
[data-theme="custom"] .hover\\:text-gray-300:hover { color: var(--text-body); }
[data-theme="custom"] .hover\\:text-gray-400:hover { color: var(--text-secondary); }
[data-theme="custom"] .hover\\:text-white:hover { color: var(--text-heading); }

[data-theme="custom"] .text-blue-400 { color: ${a.blue}; }
[data-theme="custom"] .text-emerald-400 { color: ${a.emerald}; }
[data-theme="custom"] .text-amber-400 { color: ${a.amber}; }
[data-theme="custom"] .text-purple-400 { color: ${a.purple}; }
[data-theme="custom"] .text-rose-400 { color: ${a.rose}; }
[data-theme="custom"] .text-cyan-400 { color: ${a.cyan}; }
[data-theme="custom"] .text-red-400 { color: ${a.red}; }
[data-theme="custom"] .text-indigo-300 { color: ${a.purple}; }
[data-theme="custom"] .text-indigo-400 { color: ${a.blue}; }

[data-theme="custom"] .bg-blue-900\\/40 { background-color: ${hexToRgba(a.blue, 0.15)}; }
[data-theme="custom"] .bg-emerald-900\\/40 { background-color: ${hexToRgba(a.emerald, 0.15)}; }
[data-theme="custom"] .bg-amber-900\\/40 { background-color: ${hexToRgba(a.amber, 0.16)}; }
[data-theme="custom"] .bg-purple-900\\/40 { background-color: ${hexToRgba(a.purple, 0.15)}; }
[data-theme="custom"] .bg-rose-900\\/40 { background-color: ${hexToRgba(a.rose, 0.15)}; }
[data-theme="custom"] .bg-cyan-900\\/40 { background-color: ${hexToRgba(a.cyan, 0.15)}; }

[data-theme="custom"] .bg-gray-500\\/15 { background-color: rgba(${overlayBase}, 0.10); }
[data-theme="custom"] .bg-blue-500\\/15 { background-color: ${hexToRgba(a.blue, 0.13)}; }
[data-theme="custom"] .bg-emerald-500\\/15 { background-color: ${hexToRgba(a.emerald, 0.14)}; }
[data-theme="custom"] .bg-amber-500\\/15 { background-color: ${hexToRgba(a.amber, 0.16)}; }
[data-theme="custom"] .bg-red-500\\/15 { background-color: ${hexToRgba(a.red, 0.13)}; }
[data-theme="custom"] .bg-indigo-500\\/15 { background-color: ${hexToRgba(a.purple, 0.13)}; }
[data-theme="custom"] .bg-indigo-500\\/20 { background-color: ${hexToRgba(a.purple, 0.18)}; }
[data-theme="custom"] .bg-indigo-500\\/30 { background-color: ${hexToRgba(a.purple, 0.26)}; }

[data-theme="custom"] .bg-cyan-500\\/5 { background-color: ${hexToRgba(a.cyan, 0.08)}; }
[data-theme="custom"] .border-cyan-500\\/20 { border-color: ${hexToRgba(a.cyan, 0.26)}; }
[data-theme="custom"] .bg-emerald-500\\/5 { background-color: ${hexToRgba(a.emerald, 0.08)}; }
[data-theme="custom"] .border-emerald-500\\/20 { border-color: ${hexToRgba(a.emerald, 0.26)}; }

[data-theme="custom"] .bg-indigo-600 { background-color: ${a.blue}; }
[data-theme="custom"] .hover\\:bg-indigo-500:hover { background-color: ${lighten(a.blue, 0.10)}; }
[data-theme="custom"] .bg-emerald-600 { background-color: ${a.emerald}; }
[data-theme="custom"] .hover\\:bg-emerald-500:hover { background-color: ${lighten(a.emerald, 0.10)}; }
[data-theme="custom"] .bg-red-600 { background-color: ${a.red}; }
[data-theme="custom"] .hover\\:bg-red-500:hover { background-color: ${lighten(a.red, 0.10)}; }
[data-theme="custom"] .bg-amber-500 { background-color: ${a.amber}; }
[data-theme="custom"] .bg-amber-600 { background-color: ${darken(a.amber, 0.10)}; }
`;
}

function hexToRgb(hex) {
  const m = hex.replace("#", "").match(/.{2}/g);
  if (!m) return { r: 0, g: 0, b: 0 };
  return { r: parseInt(m[0], 16), g: parseInt(m[1], 16), b: parseInt(m[2], 16) };
}

function hexToRgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function lighten(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  const t = (n) => Math.round(n + (255 - n) * amount);
  return rgbToHex(t(r), t(g), t(b));
}

function darken(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  const t = (n) => Math.round(n * (1 - amount));
  return rgbToHex(t(r), t(g), t(b));
}
